import { getTableContainerWidth } from '@atlaskit/editor-common/node-width';
import type { Node } from '@atlaskit/editor-prosemirror/model';
import { akEditorTableNumberColumnWidth } from '@atlaskit/editor-shared-styles/consts';

import { getTableEstimatedHeight } from '../../../../utils/node-estimated-height.ts';

import { getColumnCount } from './rowForms.ts';

// Measured on Confluence's renderer: 16px Atlassian Sans in table cells.
const LINE_HEIGHT = 27.4;
const CHARACTER_WIDTH = 8.2;
const BLOCK_GAP = 12;
const LIST_ITEM_GAP = 4;
const LIST_INDENT = 24;
const CELL_PADDING_X = 17;
const CELL_PADDING_Y = 16.6;
const MINIMUM_ROW_HEIGHT = 44;

const LISTS = new Set(['bulletList', 'orderedList', 'taskList', 'decisionList']);
const INDENTED_TEXT = new Set(['taskItem', 'decisionItem']);

type LeafText = (leaf: Node) => string;

/** The authored column widths, from the first row whose cells all have one. */
function getAuthoredWidths(table: Node, columnCount: number): number[] | undefined {
	for (let rowIndex = 0; rowIndex < table.childCount; rowIndex++) {
		const widths: number[] = [];
		let complete = true;
		// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity -- Visits each cell at most once.
		table.child(rowIndex).forEach((cell) => {
			const colwidth: unknown = cell.attrs.colwidth;
			const colspan = cell.attrs.colspan || 1;
			if (
				Array.isArray(colwidth) &&
				colwidth.length === colspan &&
				// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity -- A cell's colwidth has one entry per column it spans.
				colwidth.every((width) => width > 0)
			) {
				widths.push(...colwidth);
			} else {
				complete = false;
			}
		});
		if (complete && widths.length === columnCount) {
			return widths;
		}
	}
	return undefined;
}

/** The rendered width of each column, before cell padding. */
export function getColumnWidths(table: Node): number[] {
	const columnCount = Math.max(getColumnCount(table), 1);
	const tableWidth =
		getTableContainerWidth(table) -
		(table.attrs.isNumberColumnEnabled ? akEditorTableNumberColumnWidth : 0);
	const authored = getAuthoredWidths(table, columnCount);
	if (!authored) {
		return Array.from({ length: columnCount }, () => tableWidth / columnCount);
	}
	const total = authored.reduce((sum, width) => sum + width, 0);
	const scale = total > tableWidth ? tableWidth / total : 1;
	return authored.map((width) => width * scale);
}

function getTextBlockHeight(block: Node, width: number, leafText: LeafText): number {
	const segments = [0];
	block.forEach((inline) => {
		if (inline.type.name === 'hardBreak') {
			segments.push(0);
		} else {
			segments[segments.length - 1] += inline.isText
				? (inline.text?.length ?? 0)
				: leafText(inline).length;
		}
	});
	const lines = segments.reduce(
		(sum, characters) => sum + Math.max(1, Math.ceil((characters * CHARACTER_WIDTH) / width)),
		0,
	);
	return lines * LINE_HEIGHT;
}

function getBlocksHeight(container: Node, width: number, leafText: LeafText, gap: number): number {
	let height = 0;
	container.forEach((block, _offset, index) => {
		height += (index > 0 ? gap : 0) + getBlockHeight(block, width, leafText);
	});
	return height;
}

function getBlockHeight(block: Node, width: number, leafText: LeafText): number {
	const name = block.type.name;
	if (name === 'paragraph') {
		return getTextBlockHeight(block, width, leafText);
	}
	if (INDENTED_TEXT.has(name)) {
		return getTextBlockHeight(block, width - LIST_INDENT, leafText);
	}
	if (LISTS.has(name)) {
		return getBlocksHeight(block, width, leafText, LIST_ITEM_GAP);
	}
	if (name === 'listItem') {
		return getBlocksHeight(block, width - LIST_INDENT, leafText, BLOCK_GAP);
	}
	return getTableEstimatedHeight(block);
}

/** A merged row's height: its tallest cell, with each cell's text wrapped at its column's width. */
export function estimateMergedRowHeight(
	row: Node,
	columnWidths: readonly number[],
	leafText: LeafText,
): number {
	const edges = [0];
	for (const width of columnWidths) {
		edges.push(edges[edges.length - 1] + width);
	}
	let column = 0;
	let tallest = 0;
	row.forEach((cell) => {
		const end = Math.min(column + (cell.attrs.colspan || 1), columnWidths.length);
		const cellWidth = edges[end] - edges[Math.min(column, end)];
		column = end;
		const textWidth = Math.max(cellWidth - CELL_PADDING_X, CHARACTER_WIDTH);
		tallest = Math.max(tallest, getBlocksHeight(cell, textWidth, leafText, BLOCK_GAP));
	});
	return Math.round(Math.max(tallest + CELL_PADDING_Y, MINIMUM_ROW_HEIGHT));
}
